import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Phone } from 'lucide-react';
import { api } from '@/shared/api/axios';
import { Modal } from '@/shared/components/ui/Modal';
import { Button } from '@/shared/components/ui/Button';
import { Spinner } from '@/shared/components/ui/Spinner';

interface DuplicateGroup {
  phone: string;
  records: { id: string; leadNo: number | null; name: string; stageLabel: string; source: string; createdAt: string; ownerName: string }[];
}

/**
 * Open pipeline records that share a phone number (2026-11-04) — made before
 * the stronger duplicate check, for an admin to review: keep one, close the
 * rest as Lost ("duplicate") or reassign.
 */
export function DuplicatesDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const navigate = useNavigate();
  const { data, isLoading, error } = useQuery({
    queryKey: ['pipeline', 'duplicates'],
    queryFn: async () => (await api.get<{ groups: DuplicateGroup[]; extraRecords: number }>('/api/pipeline/duplicates')).data,
    enabled: open,
  });

  return (
    <Modal open={open} onClose={onClose} size="xl" title="Possible duplicates — open records with the same phone">
      {isLoading ? (
        <div className="flex justify-center py-10"><Spinner /></div>
      ) : error ? (
        <p className="text-body-sm text-danger-text">Could not load the duplicates.</p>
      ) : !data?.groups.length ? (
        <p className="py-6 text-center text-body-sm text-on-surface-variant">No open records share a phone number.</p>
      ) : (
        <div className="flex flex-col gap-3">
          <p className="text-body-sm text-on-surface-variant">
            {data.groups.length} phone number(s) have more than one open record ({data.extraRecords} extra). Open them, keep the one being worked on,
            and close the others as Lost with the reason "duplicate" — or reassign them to the same person.
          </p>
          <div className="flex max-h-[60vh] flex-col gap-3 overflow-y-auto">
            {data.groups.map((g) => (
              <div key={g.phone} className="rounded-lg border border-outline">
                <div className="flex items-center gap-2 border-b border-outline bg-surface-container-low px-3 py-1.5 text-body-sm font-semibold text-on-surface">
                  <Phone className="h-3.5 w-3.5" aria-hidden /> +91 {g.phone.slice(0, 5)} {g.phone.slice(5)} · {g.records.length} open records
                </div>
                <ul className="divide-y divide-outline/60">
                  {g.records.map((r) => (
                    <li key={r.id} className="flex items-center justify-between gap-2 px-3 py-2 text-body-sm">
                      <span className="min-w-0">
                        <span className="font-medium text-on-surface">{r.leadNo ? `#${r.leadNo} ` : ''}{r.name}</span>
                        <span className="block text-on-surface-variant">
                          {r.stageLabel}{r.ownerName ? ` · ${r.ownerName}` : ''}{r.source ? ` · ${r.source}` : ''} · {new Date(r.createdAt).toLocaleDateString('en-IN')}
                        </span>
                      </span>
                      <Button size="sm" variant="outline" onClick={() => navigate(`/pipeline/${r.id}`)}>Open</Button>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}
    </Modal>
  );
}
